import type {
  ClientRuntimeConfig,
  HomepageConfig,
  WidgetBranding,
  WidgetFeatures,
  WidgetPalette,
} from '../types'
import { getApiBase } from './config'

export type RuntimeConfig = ClientRuntimeConfig

const defaultRuntimeConfig: RuntimeConfig = {
  portalKey: 'demo-portal-key',
  welcomeAuthor: 'Sky from Support',
  welcomeText: 'Hey there! Ask me anything about wooden houses.',
  placeholder: 'Send us a message...',
  exampleMessages: [],
  exampleResponses: [],
  chatMode: 'chat',
  branding: {
    name: 'Chat Widget',
    primaryColor: '#6366f1',
    secondaryColor: '#a855f7',
    backgroundColor: '#0f172a',
    surfaceColor: '#111c36',
    fontFamily: 'Inter, sans-serif',
    launcherIcon: '💬',
    launcherIconOpen: '✕',
    headerIcon: '💬',
    headerTitle: 'Need a hand?',
    headerSubtitle: 'We typically reply in under 5 minutes.',
    customCss: '',
    palette: {
      primaryColor: '#6366f1',
      primaryHover: '#4f46e5',
      primaryLight: '#818cf8',
      secondaryColor: '#a855f7',
      secondaryLight: '#c084fc',
      backgroundColor: '#0f172a',
      backgroundAlt: '#111c36',
      surfaceColor: '#111c36',
      surfaceHover: 'rgba(17, 28, 54, 0.85)',
      borderColor: 'rgba(15, 23, 42, 0.28)',
      borderLight: 'rgba(148, 163, 184, 0.18)',
      shadowColor: 'rgba(15, 23, 42, 0.45)',
      shadowLight: 'rgba(15, 23, 42, 0.25)',
      textPrimary: '#f8fafc',
      textSecondary: 'rgba(241, 245, 249, 0.82)',
      textMuted: 'rgba(203, 213, 225, 0.65)',
      gradientStart: '#6366f1',
      gradientEnd: '#4f46e5',
    },
  },
  features: {
    enableFileUpload: false,
    enableRating: true,
    enableTranscript: true,
    showPoweredBy: true,
  },
  homepage: {
    heroTitle: 'Your new Chat Widget',
    heroSubtitle: 'Delight visitors with instant answers.',
    heroDescription:
      'Open the floating bubble to explore the interactive support experience. Chat already contains knowledge of your website.',
    primaryCta: {
      label: 'Open chat',
      action: 'toggle-chat',
    },
    secondaryCta: {
      label: 'Admin panel',
      action: 'link',
      href: '/admin',
    },
    featureHighlights: [
      'Embeddable AI support in minutes',
      'Connects to your knowledge base',
      'Fully customizable branding',
    ],
    backgroundImage: '',
    layoutVariant: 'split',
  },
}

export async function fetchRuntimeConfig(): Promise<RuntimeConfig> {
  try {
    const response = await fetch(`${getApiBase()}/config`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const text = await response.text()
      throw new Error(
        `Runtime config request failed (${response.status}): ${
          text || response.statusText
        }`
      )
    }

    const data = (await response.json()) as Partial<RuntimeConfig> | null
    if (!data) {
      return defaultRuntimeConfig
    }

    return {
      portalKey:
        normalizeString(data.portalKey) || defaultRuntimeConfig.portalKey,
      welcomeAuthor:
        normalizeString(data.welcomeAuthor) ??
        defaultRuntimeConfig.welcomeAuthor,
      welcomeText:
        normalizeString(data.welcomeText) ?? defaultRuntimeConfig.welcomeText,
      placeholder:
        normalizeString(data.placeholder) || defaultRuntimeConfig.placeholder,
      exampleMessages: Array.isArray(data.exampleMessages)
        ? data.exampleMessages.map((value) => value.toString())
        : defaultRuntimeConfig.exampleMessages,
      exampleResponses: Array.isArray(data.exampleResponses)
        ? data.exampleResponses.map((value) => value.toString())
        : defaultRuntimeConfig.exampleResponses,
      chatMode: data.chatMode === 'chat' ? 'chat' : 'assistant',
      branding: normalizeBranding(data.branding) ?? defaultRuntimeConfig.branding,
      features: normalizeFeatures(data.features) ?? defaultRuntimeConfig.features,
      homepage:
        normalizeHomepage(data.homepage) ?? defaultRuntimeConfig.homepage,
    }
  } catch (error) {
    console.error('[config] Failed to load runtime config', error)
    return defaultRuntimeConfig
  }
}

export function getDefaultRuntimeConfig(): RuntimeConfig {
  return {
    ...defaultRuntimeConfig,
    exampleMessages: [...defaultRuntimeConfig.exampleMessages],
    exampleResponses: [...defaultRuntimeConfig.exampleResponses],
    branding: defaultRuntimeConfig.branding
      ? {
          ...defaultRuntimeConfig.branding,
          palette: defaultRuntimeConfig.branding.palette
            ? { ...defaultRuntimeConfig.branding.palette }
            : undefined,
        }
      : undefined,
    features: defaultRuntimeConfig.features
      ? { ...defaultRuntimeConfig.features }
      : undefined,
    homepage: defaultRuntimeConfig.homepage
      ? {
          ...defaultRuntimeConfig.homepage,
          featureHighlights: [
            ...(defaultRuntimeConfig.homepage.featureHighlights ?? []),
          ],
          primaryCta: { ...defaultRuntimeConfig.homepage.primaryCta },
          secondaryCta: defaultRuntimeConfig.homepage.secondaryCta
            ? { ...defaultRuntimeConfig.homepage.secondaryCta }
            : undefined,
        }
      : undefined,
  }
}

function normalizeString(value: unknown): string {
  if (typeof value !== 'string') return ''
  return value.trim()
}

function normalizeBranding(value: unknown): WidgetBranding | undefined {
  if (!value || typeof value !== 'object') return undefined
  const branding = value as Record<string, unknown>
  const name = normalizeString(branding.name)
  if (!name) return undefined

  const result: WidgetBranding = {
    name,
    primaryColor:
      normalizeString(branding.primaryColor) ||
      defaultRuntimeConfig.branding?.primaryColor ||
      '#6366f1',
  }

  const secondary = normalizeString(branding.secondaryColor)
  if (secondary) result.secondaryColor = secondary
  else if (defaultRuntimeConfig.branding?.secondaryColor) {
    result.secondaryColor = defaultRuntimeConfig.branding.secondaryColor
  }

  const backgroundColor = normalizeString(branding.backgroundColor)
  if (backgroundColor) {
    result.backgroundColor = backgroundColor
  } else if (defaultRuntimeConfig.branding?.backgroundColor) {
    result.backgroundColor = defaultRuntimeConfig.branding.backgroundColor
  }

  const surfaceColor = normalizeString(branding.surfaceColor)
  if (surfaceColor) {
    result.surfaceColor = surfaceColor
  } else if (defaultRuntimeConfig.branding?.surfaceColor) {
    result.surfaceColor = defaultRuntimeConfig.branding.surfaceColor
  }

  const fontFamily = normalizeString(branding.fontFamily)
  if (fontFamily) result.fontFamily = fontFamily
  else if (defaultRuntimeConfig.branding?.fontFamily) {
    result.fontFamily = defaultRuntimeConfig.branding.fontFamily
  }

  const logo = normalizeString(branding.logo)
  if (logo) result.logo = logo

  const customCss = normalizeString(branding.customCss)
  if (customCss) result.customCss = customCss

  const palette = normalizePalette(branding.palette)
  if (palette) result.palette = palette

  const launcherIcon = normalizeString(branding.launcherIcon)
  if (launcherIcon) result.launcherIcon = launcherIcon
  else if (defaultRuntimeConfig.branding?.launcherIcon) {
    result.launcherIcon = defaultRuntimeConfig.branding.launcherIcon
  }

  const launcherIconOpen = normalizeString(branding.launcherIconOpen)
  if (launcherIconOpen) result.launcherIconOpen = launcherIconOpen
  else if (defaultRuntimeConfig.branding?.launcherIconOpen) {
    result.launcherIconOpen = defaultRuntimeConfig.branding.launcherIconOpen
  }

  const headerIcon = normalizeString(branding.headerIcon)
  if (headerIcon) result.headerIcon = headerIcon
  else if (defaultRuntimeConfig.branding?.headerIcon) {
    result.headerIcon = defaultRuntimeConfig.branding.headerIcon
  }

  const headerTitle = normalizeString(branding.headerTitle)
  if (headerTitle) result.headerTitle = headerTitle
  else if (defaultRuntimeConfig.branding?.headerTitle) {
    result.headerTitle = defaultRuntimeConfig.branding.headerTitle
  }

  const headerSubtitle = normalizeString(branding.headerSubtitle)
  if (headerSubtitle) result.headerSubtitle = headerSubtitle
  else if (defaultRuntimeConfig.branding?.headerSubtitle) {
    result.headerSubtitle = defaultRuntimeConfig.branding.headerSubtitle
  }

  return result
}

function normalizeFeatures(value: unknown): WidgetFeatures | undefined {
  if (!value || typeof value !== 'object') return undefined
  const features = value as Record<string, unknown>
  return {
    enableFileUpload: Boolean(features.enableFileUpload),
    enableRating:
      typeof features.enableRating === 'boolean'
        ? features.enableRating
        : defaultRuntimeConfig.features?.enableRating ?? true,
    enableTranscript:
      typeof features.enableTranscript === 'boolean'
        ? features.enableTranscript
        : defaultRuntimeConfig.features?.enableTranscript ?? true,
    showPoweredBy:
      typeof features.showPoweredBy === 'boolean'
        ? features.showPoweredBy
        : defaultRuntimeConfig.features?.showPoweredBy ?? true,
  }
}

function normalizePalette(value: unknown): WidgetPalette | undefined {
  if (!value || typeof value !== 'object') return undefined
  const palette = value as Record<string, unknown>
  const entries: [keyof WidgetPalette, string][] = [
    ['primaryColor', 'primaryColor'],
    ['primaryHover', 'primaryHover'],
    ['primaryLight', 'primaryLight'],
    ['secondaryColor', 'secondaryColor'],
    ['secondaryLight', 'secondaryLight'],
    ['backgroundColor', 'backgroundColor'],
    ['backgroundAlt', 'backgroundAlt'],
    ['surfaceColor', 'surfaceColor'],
    ['surfaceHover', 'surfaceHover'],
    ['borderColor', 'borderColor'],
    ['borderLight', 'borderLight'],
    ['shadowColor', 'shadowColor'],
    ['shadowLight', 'shadowLight'],
    ['textPrimary', 'textPrimary'],
    ['textSecondary', 'textSecondary'],
    ['textMuted', 'textMuted'],
    ['gradientStart', 'gradientStart'],
    ['gradientEnd', 'gradientEnd'],
    ['userMessageBg', 'userMessageBg'],
    ['userMessageText', 'userMessageText'],
    ['userMessageShadow', 'userMessageShadow'],
    ['agentMessageBg', 'agentMessageBg'],
    ['agentMessageText', 'agentMessageText'],
    ['headerTitleColor', 'headerTitleColor'],
    ['headerSubtitleColor', 'headerSubtitleColor'],
    ['launcherBg', 'launcherBg'],
    ['launcherIcon', 'launcherIcon'],
    ['sendButtonBg', 'sendButtonBg'],
    ['sendButtonIcon', 'sendButtonIcon'],
  ]

  const result: WidgetPalette = {}
  let hasValue = false
  for (const [key, sourceKey] of entries) {
    const raw = palette[sourceKey]
    if (typeof raw === 'string' && raw.trim().length > 0) {
      result[key] = raw.trim()
      hasValue = true
    }
  }

  return hasValue ? result : undefined
}

function normalizeHomepageCta(value: unknown): HomepageConfig['primaryCta'] | undefined {
  if (!value || typeof value !== 'object') return undefined
  const cta = value as Record<string, unknown>
  const label = normalizeString(cta.label)
  if (!label) return undefined

  const actionRaw = normalizeString(cta.action)
  const action: 'toggle-chat' | 'link' =
    actionRaw === 'link' ? 'link' : 'toggle-chat'

  if (action === 'link') {
    const href = normalizeString(cta.href)
    if (!href) {
      return undefined
    }
    return { label, action, href }
  }

  return { label, action }
}

function normalizeHomepage(value: unknown): HomepageConfig | undefined {
  if (!value || typeof value !== 'object') return undefined
  const data = value as Record<string, unknown>

  const heroTitle =
    normalizeString(data.heroTitle) ||
    defaultRuntimeConfig.homepage?.heroTitle ||
    'Your new Chat Widget'
  const heroSubtitle = normalizeString(data.heroSubtitle)
  const heroDescription = normalizeString(data.heroDescription)

  const primaryCta =
    normalizeHomepageCta(data.primaryCta) ??
    defaultRuntimeConfig.homepage?.primaryCta
  if (!primaryCta) {
    return undefined
  }

  const hasSecondary = Object.prototype.hasOwnProperty.call(
    data,
    'secondaryCta'
  )
  const secondaryCta = normalizeHomepageCta(data.secondaryCta)

  const featureHighlights = Array.isArray(data.featureHighlights)
    ? data.featureHighlights
        .map((item) => normalizeString(item))
        .filter((item) => item.length > 0)
    : defaultRuntimeConfig.homepage?.featureHighlights ?? []

  const backgroundImage = normalizeString(data.backgroundImage)
  const layoutVariant =
    data.layoutVariant === 'centered' ? 'centered' : 'split'

  const result: HomepageConfig = {
    heroTitle,
    heroSubtitle:
      heroSubtitle ||
      defaultRuntimeConfig.homepage?.heroSubtitle ||
      undefined,
    heroDescription:
      heroDescription ||
      defaultRuntimeConfig.homepage?.heroDescription ||
      undefined,
    primaryCta,
    secondaryCta:
      secondaryCta ??
      defaultRuntimeConfig.homepage?.secondaryCta ??
      undefined,
    featureHighlights,
    backgroundImage: backgroundImage || undefined,
    layoutVariant,
  }

  if (hasSecondary) {
    result.secondaryCta = secondaryCta ?? null
  } else if (defaultRuntimeConfig.homepage?.secondaryCta) {
    result.secondaryCta = defaultRuntimeConfig.homepage.secondaryCta
  }

  return result
}
